import { EMPTY, of, concat } from 'rxjs';
import { take, mergeMap, switchMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../store/global-selectors';
import * as PagingActions from '../../../store/paging/paging.actions';
import * as BatchActions from '../../../store/batches/batches.actions';
import * as BulkActions from '../../../store/bulk/bulk.actions';
import * as PrintActions from '../../../store/printing/print.actions';
import * as BatchNoteActions from '../../../store/batches/notes/batch-note.actions';
import * as Actions from './batches.grid.actions';
import * as Api from './batches.grid.api';
import { getTabFilterFromPathName } from '../../../services/batch-grid-service';
import { BatchesAdvancedSearch } from '../../../components/shipping-grid/views/batches-advanced-search';
import { batchRowsGetReplay } from '../../../store/batches/batches.grid.observables';
import { getSelectedViewFromPathName } from '../../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import { bulkLoadFulfillmentPlanSets$ } from '../../bulkload/bulkload.observables';

const pagingViewName = 'batches.grid';

export const batchRowsGet = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(
      Actions.GET_BATCH_ROW_REQUEST, // multiple of these actions are triggered in quick sucession from batches-list-page because the dispatch is wrapped in a useEffect. EJG: We cannot use throttle because that breaks functionality, we need to reduce renders to fix this.
      Actions.ARCHIVE_BATCHES_SUCCESS,
      BatchActions.CREATE_BATCH_SUCCESS,
      BatchActions.SAVE_BATCH_NAME_CHANGE_SUCCESS,
      Actions.UPDATE_BATCH_GRID_SORTING,
      Actions.GRID_FILTER_SEARCH,
      PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS,
      PrintActions.MARK_PICK_LIST_PRINTED_SUCCESS,
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      BulkActions.BULK_PROCESSING_CREATE_LABEL_FINAL_MESSAGE,
      BatchNoteActions.SAVE_OR_EDIT_BATCH_NOTE_SUCCESS,
    ),
    switchMap(() => {
      return batchRowsGetReplay.pipe(
        take(1),
        mergeMap((filter) => {
          const state = getState();
          const pagingStats =
            Global.selectPagingViewPagingStats(state, {
              viewName: pagingViewName,
            }) ?? Object.fromEntries(new URLSearchParams(history.location.search));
          const viewId = getSelectedViewFromPathName(history?.location?.pathname)?.id;
          if (!pagingStats || viewId === BatchesAdvancedSearch.id) {
            return EMPTY;
          }
          const { page, pageSize } = pagingStats;
          const tabFilter = getTabFilterFromPathName(history);
          const selectedSortBy = Global.selectBatchesGridSortBy(state);
          const termInSearchBar = Global.selectBatchesGridSearchTerm(state);
          const trimmedSearchTerm = termInSearchBar.trim();
          const searchTerm = trimmedSearchTerm.length >= 3 ? trimmedSearchTerm : undefined;
          const apiAction = filter ? Api.getFilteredBatchRows : Api.getBatchRows;
          return apiAction(page, pageSize, tabFilter, selectedSortBy, searchTerm, filter).pipe(
            mergeMap((batchData) => {
              const { totalCount, pageData } = batchData;
              // NOTE: SPD-9235 - This is a ephemeral piece of logic that patches fulfillmentPlanSets within entities until we consolidate the logic to use entities.
              const totalRecords = totalCount;
              return concat(
                bulkLoadFulfillmentPlanSets$(pageData?.map((fps) => fps.fulfillmentPlanSetId)),
                of(PagingActions.setTotalRecords(pagingViewName, totalRecords)),
                of(Actions.getBatchRowsSuccess(batchData)),
              );
            }),
            catchError((e) => of(Actions.getBatchRowsFailure(e))),
          );
        }),
      );
    }),
  );
};

export const batchRowLoad = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_BATCH_ROW_SUCCESS),
    mergeMap((action) => {
      const batchData = get(action, 'batchData', emptyArray);
      return of(Actions.setActiveBatches(batchData));
    }),
  );
};

export const archiveBatches = (action$) => {
  return action$.pipe(
    ofType(Actions.ARCHIVE_BATCHES_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds } = action;
      return Api.archiveBatches(fulfillmentPlanSetIds).pipe(
        mergeMap(() => {
          return of(Actions.archiveBatchesSuccess());
        }),
        catchError((e) => of(Actions.archiveBatchesFailure(e))),
      );
    }),
  );
};

export const validateBatchesAddressesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_BATCHES_ADDRESSES_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds } = action;
      return Api.validateBatchesAddresses(fulfillmentPlanSetIds).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const sendEmailForBatchesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SEND_EMAIL_FOR_BATCHES_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds } = action;
      return Api.sendEmailForBatches(fulfillmentPlanSetIds).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const sendNotificationsForBatchesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SEND_NOTIFICATIONS_FOR_BATCHES_REQUEST),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds } = action;
      return Api.sendNotificationsForBatches(fulfillmentPlanSetIds).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const updateSearchTermFromGridEpic = (action$) =>
  action$.pipe(
    ofType(Actions.UPDATE_FILTER_SEARCH_TERM),
    mergeMap(() => of(Actions.gridSearch())),
  );
